/* v6-search.js — inline header search using Shopify Predictive Search.
   Partitions product results into Toys / Lubes & Care / DVDs (DVDs visually deprioritized).
   Also renders Collections and Pages/Journal articles in sitewide mode.
   On collection pages: scope toggle lets customers search within the current collection only.
   Surfaces a "magic-wand-alternative" callout when query matches magic wand.
   Collection thumbnails: falls back to first-product image (fetched async), then cobalt placeholder.
   Collections de-duped by title (shorter handle = primary).
*/
(function () {
  'use strict';
  if (window.__v6SearchInit) return;
  window.__v6SearchInit = true;

  var SEARCH = document.getElementById('v6-search');
  if (!SEARCH) return;
  var input = SEARCH.querySelector('[data-v6-search-input]');
  var panel = SEARCH.querySelector('[data-v6-search-panel]');
  var scopeBar = SEARCH.querySelector('[data-v6-scope-bar]');
  var scopeNameEl = scopeBar && SEARCH.querySelector('[data-v6-scope-name]');
  var toggles = document.querySelectorAll('[data-v6-search-toggle]');
  var closers = document.querySelectorAll('[data-v6-search-close]');
  var backdrop = document.querySelector('[data-v6-search-backdrop]');
  var debounceT;

  // Cache for collection first-product images (keyed by collection handle)
  var collImageCache = {};

  // Collection scope — populated from body data attributes on collection pages
  var collectionHandle = document.body.getAttribute('data-v6-collection-handle') || '';
  var collectionTitle = document.body.getAttribute('data-v6-collection-title') || '';
  var scopeMode = collectionHandle ? 'collection' : 'all';
  // Set of product handles in the current collection, fetched async on page load
  var collectionProductHandles = null;

  // ── Open / close ──────────────────────────────────────────────────────────

  function open() {
    SEARCH.setAttribute('data-open', 'true');
    SEARCH.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-v6-search-open');
    setTimeout(function () { input.focus(); }, 50);
  }
  function close() {
    SEARCH.setAttribute('data-open', 'false');
    SEARCH.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-v6-search-open');
  }

  toggles.forEach(function (t) { t.addEventListener('click', function (e) { e.preventDefault(); open(); }); });
  closers.forEach(function (t) { t.addEventListener('click', function (e) { e.preventDefault(); close(); }); });
  if (backdrop) backdrop.addEventListener('click', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && SEARCH.getAttribute('data-open') === 'true') close();
  });

  // ── Scope toggle (collection pages only) ─────────────────────────────────

  if (collectionHandle && scopeBar && scopeNameEl) {
    scopeBar.style.display = '';
    scopeNameEl.textContent = collectionTitle || collectionHandle;
    input.placeholder = 'Search within ' + (collectionTitle || collectionHandle) + '…';

    // Pre-fetch product handles for this collection so we can filter client-side
    fetch('/collections/' + collectionHandle + '/products.json?fields=handle&limit=250')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.products) {
          collectionProductHandles = new Set(data.products.map(function (p) { return p.handle; }));
          // Re-render current results if search is already open
          var q = input.value.trim();
          if (q.length >= 2 && SEARCH.getAttribute('data-open') === 'true') doSearch(q);
        }
      })
      .catch(function () {});

    var scopeBtns = scopeBar.querySelectorAll('[data-v6-scope]');
    scopeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        scopeMode = btn.getAttribute('data-v6-scope');
        scopeBtns.forEach(function (b) { b.classList.toggle('active', b === btn); });
        if (scopeMode === 'collection') {
          input.placeholder = 'Search within ' + (collectionTitle || collectionHandle) + '…';
        } else {
          input.placeholder = 'Search toys, films, or articles…';
        }
        var q = input.value.trim();
        if (q.length >= 2) doSearch(q);
        else panel.innerHTML = '';
      });
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function moneyFormat(price) {
    return '$' + parseFloat(price || 0).toFixed(2);
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }

  function isDVD(p) {
    var pt = (p.product_type || p.type || '').toLowerCase();
    if (pt === 'dvd' || pt === 'all dvds') return true;
    return (p.tags || []).some(function (t) { return /(^|\b)dvd(\b|$)/i.test(t) || /product-type:dvd/i.test(t); });
  }
  function isCare(p) {
    var pt = (p.product_type || p.type || '').toLowerCase();
    return ['lubricant', 'lube', 'cleaner', 'toy care'].indexOf(pt) !== -1;
  }
  function isMagicWandAlt(p) {
    return (p.tags || []).some(function (t) { return /magic-wand-alternative/i.test(t); });
  }

  function getProductHandle(p) {
    return (p.url || '').replace('/products/', '').split('?')[0].split('/')[0];
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  function renderProductItem(p) {
    var price = (p.price !== undefined) ? moneyFormat(p.price) : '';
    var img = p.featured_image && p.featured_image.url
      ? p.featured_image.url + (p.featured_image.url.indexOf('?') >= 0 ? '&' : '?') + 'width=120'
      : '';
    return '<li class="v6-search__item">' +
      '<a href="' + escapeHtml(p.url) + '">' +
        (img
          ? '<span class="v6-search__thumb" style="background-image: url(\'' + escapeHtml(img) + '\');"></span>'
          : '<span class="v6-search__thumb"></span>') +
        '<span class="v6-search__meta">' +
          '<p class="v6-search__title">' + escapeHtml(p.title) + '</p>' +
          (price ? '<p class="v6-search__price">' + price + '</p>' : '') +
        '</span>' +
      '</a>' +
    '</li>';
  }

  function renderCollectionItem(c) {
    // Shopify suggest API returns image as { url } (not .src); also check featured_image for safety
    var imgUrl = '';
    if (c.image && c.image.url) imgUrl = c.image.url;
    else if (c.featured_image && c.featured_image.url) imgUrl = c.featured_image.url;
    if (imgUrl) imgUrl = imgUrl + (imgUrl.indexOf('?') >= 0 ? '&' : '?') + 'width=120';

    var handle = (c.url || '').replace('/collections/', '').split('?')[0];

    // Check cache for first-product fallback
    if (!imgUrl && collImageCache[handle]) imgUrl = collImageCache[handle];

    // Thumb: real image > cobalt brand placeholder (async first-product fetch will upgrade it)
    var thumbStyle = imgUrl
      ? ' style="background-image: url(\'' + escapeHtml(imgUrl) + '\');"'
      : ' style="background-color: #1A3BD1;"';

    return '<a class="v6-search__coll-item" href="' + escapeHtml(c.url) + '"' +
      ' data-v6-coll-handle="' + escapeHtml(handle) + '">' +
      '<span class="v6-search__coll-thumb"' + thumbStyle + '></span>' +
      '<span class="v6-search__coll-title">' + escapeHtml(c.title) + '</span>' +
    '</a>';
  }

  // Async: for each rendered collection without an image, fetch first-product image and swap in
  function loadCollectionImages() {
    var items = panel.querySelectorAll('[data-v6-coll-handle]');
    items.forEach(function (item) {
      var thumb = item.querySelector('.v6-search__coll-thumb');
      if (!thumb || thumb.style.backgroundImage) return; // already has an image

      var handle = item.getAttribute('data-v6-coll-handle');
      if (!handle) return;

      // Already cached from a previous search
      if (collImageCache[handle]) {
        thumb.style.backgroundImage = 'url(\'' + collImageCache[handle] + '\')';
        thumb.style.backgroundColor = '';
        return;
      }

      // Storefront products.json returns images[] not featured_image
      fetch('/collections/' + handle + '/products.json?limit=1')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var p = data && data.products && data.products[0];
          var src = p && p.images && p.images[0] && p.images[0].src;
          if (!src) return;
          var imgUrl = src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=120';
          collImageCache[handle] = imgUrl;
          // Re-check element is still in panel (user may have typed more)
          var live = panel.querySelector('[data-v6-coll-handle="' + handle + '"] .v6-search__coll-thumb');
          if (live && !live.style.backgroundImage) {
            live.style.backgroundImage = 'url(\'' + imgUrl + '\')';
            live.style.backgroundColor = '';
          }
        })
        .catch(function () {});
    });
  }

  function renderPageItem(pg) {
    return '<li class="v6-search__page-item"><a href="' + escapeHtml(pg.url) + '">' + escapeHtml(pg.title) + '</a></li>';
  }

  // ── Main render ───────────────────────────────────────────────────────────

  function renderPanel(query, products, collections, pages) {
    // In scoped mode, filter to products in current collection
    if (scopeMode === 'collection' && collectionProductHandles) {
      products = products.filter(function (p) {
        return collectionProductHandles.has(getProductHandle(p));
      });
    }

    // De-dupe collections by title (case-insensitive). Sort by handle length first so
    // shorter handles (e.g. "vibrators") beat longer aliases (e.g. "vibrators-1").
    if (collections && collections.length > 1) {
      collections = collections.slice().sort(function (a, b) {
        var ha = (a.url || '').replace('/collections/', '');
        var hb = (b.url || '').replace('/collections/', '');
        return ha.length - hb.length;
      });
      var seenTitles = {};
      collections = collections.filter(function (c) {
        var key = (c.title || '').toLowerCase();
        if (seenTitles[key]) return false;
        seenTitles[key] = true;
        return true;
      });
    }

    var hasProducts = products && products.length > 0;
    var hasCollections = scopeMode === 'all' && collections && collections.length > 0;
    var hasPages = pages && pages.length > 0;

    if (!hasProducts && !hasCollections && !hasPages) {
      var scopeHint = (scopeMode === 'collection' && collectionTitle)
        ? ' in ' + escapeHtml(collectionTitle)
        : '';
      panel.innerHTML = '<p class="v6-search__empty">No matches' + scopeHint + '. Try a brand, a body part, or a feeling.</p>';
      return;
    }

    var toys = [], care = [], dvds = [], altPick = null;
    var q = (query || '').toLowerCase();
    var isMagicQuery = /\bmagic\b|\bwand/.test(q);

    (products || []).forEach(function (p) {
      if (isDVD(p)) dvds.push(p);
      else if (isCare(p)) care.push(p);
      else toys.push(p);
      if (isMagicQuery && isMagicWandAlt(p) && !altPick) altPick = p;
    });

    var html = '';

    if (toys.length) {
      var label = (scopeMode === 'collection' && collectionTitle) ? escapeHtml(collectionTitle) : 'Toys';
      html += '<div class="v6-search__section-h">' + label + ' (' + toys.length + ')</div>';
      html += '<ul class="v6-search__list">' + toys.slice(0, 8).map(renderProductItem).join('') + '</ul>';
    }
    if (isMagicQuery && altPick) {
      html += '<div class="v6-search__alt-callout">' +
        '<p class="v6-search__alt-h">Looking for something more affordable?</p>' +
        '<ul class="v6-search__list">' + renderProductItem(altPick) + '</ul>' +
      '</div>';
    }
    if (care.length) {
      html += '<div class="v6-search__section-h">Lubes &amp; care (' + care.length + ')</div>';
      html += '<ul class="v6-search__list">' + care.slice(0, 6).map(renderProductItem).join('') + '</ul>';
    }
    if (dvds.length) {
      html += '<div class="v6-search__section-h">DVDs (' + dvds.length + ')</div>';
      html += '<ul class="v6-search__list v6-search__list--small">' + dvds.slice(0, 6).map(renderProductItem).join('') + '</ul>';
    }

    if (hasCollections) {
      html += '<div class="v6-search__section-h">Collections</div>';
      html += '<div class="v6-search__coll-grid">' + collections.slice(0, 4).map(renderCollectionItem).join('') + '</div>';
    }

    if (hasPages) {
      html += '<div class="v6-search__section-h">From the journal</div>';
      html += '<ul class="v6-search__pages-list">' + pages.slice(0, 4).map(renderPageItem).join('') + '</ul>';
    }

    html += '<a class="v6-search__viewall" href="/search?q=' + encodeURIComponent(query) + '">View all results for &ldquo;' + escapeHtml(query) + '&rdquo; &rarr;</a>';
    panel.innerHTML = html;
    // Async-upgrade collection thumbnails with first-product images where missing
    if (hasCollections) loadCollectionImages();
  }

  // ── Fetch + debounce ──────────────────────────────────────────────────────

  async function doSearch(q) {
    if (!q || q.length < 2) { panel.innerHTML = ''; return; }
    try {
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page,article' +
        '&resources[limit]=10' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
      var res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      var data = await res.json();
      var results = (data && data.resources && data.resources.results) || {};
      var products = results.products || [];
      var collections = results.collections || [];
      var pagesAndArticles = (results.pages || []).concat(results.articles || []);
      renderPanel(q, products, collections, pagesAndArticles);
    } catch (e) {
      panel.innerHTML = '<p class="v6-search__empty">Search hit an error. Try again.</p>';
    }
  }

  input.addEventListener('input', function () {
    clearTimeout(debounceT);
    var q = input.value.trim();
    debounceT = setTimeout(function () { doSearch(q); }, 200);
  });

  // ── In-page collection search bar (Brief #84, refactored Brief #86) ─────────────────
  // Approach B: pre-fetch ALL products in the collection via products.json?limit=250,
  // then do fast client-side string matching on title/vendor/type/tags.
  // No per-keystroke API calls; all collection products are always searchable.

  var collSearchInput = document.querySelector('[data-v6-coll-search-input]');
  if (collSearchInput) {
    var inPageScopeHandle = collSearchInput.getAttribute('data-scope-handle') || '';
    var inPageScopeTitle  = collSearchInput.getAttribute('data-scope-title') || '';
    var inPageResultsEl   = document.querySelector('[data-v6-coll-search-results]');
    var inPageClearBtn    = document.querySelector('[data-v6-coll-search-clear]');
    var inPageDebounce;
    var inPageProducts = null; // null = still loading, Array = ready

    function syncInPageProducts() {
      if (!inPageScopeHandle) return;
      fetch('/collections/' + inPageScopeHandle + '/products.json?limit=250')
        .then(function (r) { return r.json(); })
        .then(function (d) {
          inPageProducts = (d && d.products) ? d.products : [];
          // Re-run if user already typed a query
          var q = collSearchInput.value.trim();
          if (q.length >= 2) doInPageSearch(q);
        })
        .catch(function () { inPageProducts = []; });
    }
    syncInPageProducts();

    function inPageShowResults(html) {
      if (!inPageResultsEl) return;
      inPageResultsEl.innerHTML = html;
      inPageResultsEl.removeAttribute('hidden');
    }
    function inPageHideResults() {
      if (!inPageResultsEl) return;
      inPageResultsEl.innerHTML = '';
      inPageResultsEl.setAttribute('hidden', '');
    }

    function matchesQuery(product, q) {
      var lq = q.toLowerCase();
      if ((product.title || '').toLowerCase().indexOf(lq) >= 0) return true;
      if ((product.vendor || '').toLowerCase().indexOf(lq) >= 0) return true;
      if ((product.product_type || '').toLowerCase().indexOf(lq) >= 0) return true;
      return (product.tags || []).some(function (t) { return t.toLowerCase().indexOf(lq) >= 0; });
    }

    // Wrap the matched fragment in <em> for italic-cobalt highlight treatment
    function highlightMatch(title, q) {
      if (!q || !title) return escapeHtml(title || '');
      var idx = title.toLowerCase().indexOf(q.toLowerCase());
      if (idx < 0) return escapeHtml(title);
      return escapeHtml(title.slice(0, idx)) +
        '<em>' + escapeHtml(title.slice(idx, idx + q.length)) + '</em>' +
        escapeHtml(title.slice(idx + q.length));
    }

    function renderInPageItem(p, q) {
      var price = (p.variants && p.variants[0]) ? moneyFormat(p.variants[0].price) : '';
      var imgSrc = (p.images && p.images[0] && p.images[0].src) ? p.images[0].src : '';
      if (imgSrc) imgSrc = imgSrc + (imgSrc.indexOf('?') >= 0 ? '&' : '?') + 'width=120';
      var href = '/products/' + encodeURIComponent(p.handle) +
        '?bc_from=' + encodeURIComponent(inPageScopeHandle) + '&bc_via=search';
      return '<a class="v6-coll-search__item" href="' + href + '">' +
        (imgSrc
          ? '<span class="v6-coll-search__thumb" style="background-image:url(\'' + escapeHtml(imgSrc) + '\');"></span>'
          : '<span class="v6-coll-search__thumb"></span>') +
        '<span class="v6-coll-search__meta">' +
          '<span class="v6-coll-search__title">' + highlightMatch(p.title, q) + '</span>' +
          (price ? '<span class="v6-coll-search__price">' + price + '</span>' : '') +
        '</span>' +
      '</a>';
    }

    function doInPageSearch(q) {
      if (!q || q.length < 2) { inPageHideResults(); return; }

      // Products not yet loaded — show a placeholder; syncInPageProducts will re-invoke when ready
      if (inPageProducts === null) {
        inPageShowResults('<div class="v6-coll-search__loading">Loading…</div>');
        return;
      }

      var matched = inPageProducts.filter(function (p) { return matchesQuery(p, q); });
      var count = matched.length;

      if (count === 0) {
        inPageShowResults(
          '<div class="v6-coll-search__head">0 matches · ' + escapeHtml(inPageScopeTitle) + '</div>' +
          '<div class="v6-coll-search__empty-wrap">' +
            '<p class="v6-coll-search__empty-title">Nothing for “' + escapeHtml(q) + '” here.</p>' +
            '<p class="v6-coll-search__empty-body">Try <a class="v6-coll-search__empty-link" href="/search?q=' +
              encodeURIComponent(q) + '&type=product">all of LovingSex →</a></p>' +
          '</div>'
        );
        return;
      }

      var shown = matched.slice(0, 8);
      var label = count + ' match' + (count !== 1 ? 'es' : '') + ' · ' + escapeHtml(inPageScopeTitle);
      inPageShowResults(
        '<div class="v6-coll-search__head">' + label + '</div>' +
        shown.map(function (p) { return renderInPageItem(p, q); }).join('') +
        (count > 8
          ? '<a class="v6-coll-search__viewall" href="/collections/' + escapeHtml(inPageScopeHandle) +
              '?q=' + encodeURIComponent(q) + '">+' + (count - 8) + ' more in ' +
              escapeHtml(inPageScopeTitle) + ' →</a>'
          : '')
      );
    }

    collSearchInput.addEventListener('input', function () {
      clearTimeout(inPageDebounce);
      var q = collSearchInput.value.trim();
      if (inPageClearBtn) inPageClearBtn.hidden = !q;
      inPageDebounce = setTimeout(function () { doInPageSearch(q); }, 250);
    });

    if (inPageClearBtn) {
      inPageClearBtn.addEventListener('click', function () {
        collSearchInput.value = '';
        inPageClearBtn.hidden = true;
        inPageHideResults();
        collSearchInput.focus();
      });
    }

    // Close dropdown on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-v6-coll-search]')) inPageHideResults();
    });
  }
})();
